import type {
  MorphemeGlyphTileGraphemeMorphemeSlice,
  MorphemeGlyphTileWordMorphemeSlice,
} from "@-/dev.improve.haniverse.lib.morpheme.entities";
import {
  EMPTY_PINYIN_LIST,
  type GraphemePinyinLookupMap,
  readGraphemePinyinLookup,
  useGraphemePinyinQueryOptionsList,
} from "@-/dev.improve.haniverse.lib.morpheme.grapheme.pinyin.dots-lookup";
import type { ContentDatabase } from "@-/dev.improve.haniverse.database.content";
import { uniq } from "@-/com.npmjs.array-utils-ts";
import { ContentDatabaseContext } from "@-/dev.improve.haniverse.contexts.database.content";
import { queryOptions, useQueries, useQuery } from "@-/com.npmjs.tanstack__react-query";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import runes from "@-/com.npmjs.runes2";
import { useMemo } from "react";
import { hasLength } from "@-/com.npmjs.ts-array-length";
import { isErhuaCharacter } from "@-/dev.improve.haniverse.data.erhua-words";
import { ERHUA_PINYIN } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.tile.ui.glyphs._components.content";
import { getTileContentDatabaseId } from "#get-tile-content-database-id";

function pinyinReadingsFromPrevalentList(
  prevalentPinyinList: readonly string[],
  primaryPinyin?: string,
): { additionalPinyin: string[]; primaryPinyin: string | null } {
  if (hasLength(prevalentPinyinList, 0)) {
    return { additionalPinyin: [], primaryPinyin: null };
  }
  const primary = primaryPinyin ?? prevalentPinyinList[0] ?? "";
  const additionalPinyin = prevalentPinyinList.filter((reading) => reading !== primary);
  return { additionalPinyin, primaryPinyin: primary };
}

export function useMorphemeGlyphTileGraphemeMorphemeSlice(
  grapheme: string,
  primaryPinyin?: string,
): MorphemeGlyphTileGraphemeMorphemeSlice {
  const pinyinList =
    useQueries({ queries: useGraphemePinyinQueryOptionsList([grapheme]) })[0]?.data ??
    EMPTY_PINYIN_LIST;

  return useMemo(() => {
    const { additionalPinyin, primaryPinyin: resolvedPrimaryPinyin } =
      pinyinReadingsFromPrevalentList(pinyinList, primaryPinyin);
    return {
      additionalPinyin,
      grapheme,
      morphemeType: "grapheme",
      primaryPinyin: resolvedPrimaryPinyin,
    };
  }, [grapheme, pinyinList, primaryPinyin]);
}

const EMPTY_SLICE_MAP: ReadonlyMap<string, MorphemeGlyphTileGraphemeMorphemeSlice> = new Map();

/**
 * The content-DB pinyin lookup for a set of graphemes, as `queryOptions` keyed on the deduped,
 * sorted set — shared by the batched grid hook below and by paged grids
 * (`usePagedGraphemeTileData`), so a page fetched by one is a cache hit for the other. The content
 * DB is immutable for the app's lifetime, so there is no change-version.
 */
export function graphemePinyinLookupQueryOptions(
  contentDb: ContentDatabase,
  uniqueGraphemes: readonly string[],
) {
  return queryOptions({
    queryFn: () => readGraphemePinyinLookup(contentDb, uniqueGraphemes),
    queryKey: [
      "graphemePinyinLookup",
      getTileContentDatabaseId(contentDb),
      ...uniqueGraphemes,
    ] as const,
  });
}

/** Build every grapheme's tile slice from the pinyin lookup — pure, shared with the paged grid. */
export function buildGraphemeMorphemeSliceByGrapheme(
  uniqueGraphemes: readonly string[],
  pinyinByGrapheme: GraphemePinyinLookupMap,
): ReadonlyMap<string, MorphemeGlyphTileGraphemeMorphemeSlice> {
  const map = new Map<string, MorphemeGlyphTileGraphemeMorphemeSlice>();
  for (const grapheme of uniqueGraphemes) {
    const pinyinList = pinyinByGrapheme.get(grapheme) ?? EMPTY_PINYIN_LIST;
    const { additionalPinyin, primaryPinyin } = pinyinReadingsFromPrevalentList(pinyinList);
    map.set(grapheme, {
      additionalPinyin,
      grapheme,
      morphemeType: "grapheme",
      primaryPinyin,
    });
  }
  return map;
}

/**
 * BATCHED variant of {@link useMorphemeGlyphTileGraphemeMorphemeSlice}: ONE `useQuery` over a
 * whole set of graphemes, returning a `grapheme → slice` map. A grid that would otherwise mount one
 * per-tile `useMorphemeGlyphTileGraphemeMorphemeSlice` lifts this once at the parent and passes
 * each tile its slice as a prop.
 *
 * IMPORTANT: this uses a SINGLE `useQuery` over the whole set (via `readGraphemePinyinLookup`), NOT
 * `useQueries` over N per-grapheme options. `useQueries` returns a fresh results array as EACH of the
 * N reads resolves, so the derived map's identity changed on every individual resolution and every
 * consuming grid re-rendered N times — a re-render storm that (per level, across the keep-alive
 * sub-routes) blocked the JS thread for ~2s and left Browse blank. One query resolves ONCE, so the
 * map is built once and the grids render once.
 *
 * The content DB is immutable for the app's lifetime, so there's no change-version. Graphemes are
 * de-duplicated and sorted into a stable key; every grapheme is present in the returned map.
 */
export function useMorphemeGlyphTileGraphemeMorphemeSlices(
  graphemes: readonly string[],
): ReadonlyMap<string, MorphemeGlyphTileGraphemeMorphemeSlice> {
  const [contentDb] = useContextSelectors(ContentDatabaseContext, (c) => c.db);
  const uniqueGraphemes = useMemo(() => uniq([...graphemes]).sort(), [graphemes]);

  const { data: pinyinByGrapheme } = useQuery(
    graphemePinyinLookupQueryOptions(contentDb, uniqueGraphemes),
  );

  return useMemo(
    () =>
      pinyinByGrapheme === undefined
        ? EMPTY_SLICE_MAP
        : buildGraphemeMorphemeSliceByGrapheme(uniqueGraphemes, pinyinByGrapheme),
    [pinyinByGrapheme, uniqueGraphemes],
  );
}

/**
 * Build a word tile's slice from its graphemes and a `grapheme → primary pinyin` resolver. Pure —
 * shared by the self-resolving per-tile hook below and by batched grids (`useBatchedWordTileData`)
 * that resolve pinyin ONCE for a whole catalog and build slices lazily per rendered word.
 *
 * A grapheme the resolver returns `null` for (pending batch, no pronunciation card) renders with
 * the bundled dictionary tone color instead of a query-resolved one — the glyph is always drawn.
 * An erhua 儿 (一点儿) takes its `r0` reading instead of 儿's primary `er2`, without waiting on the
 * batch, so a pinyin front folds it into the syllable before it from the first render.
 */
export function buildWordMorphemeGlyphTileSlice(
  simplified: string,
  getPrimaryPinyin: (grapheme: string) => string | null,
): MorphemeGlyphTileWordMorphemeSlice {
  const graphemeList = runes(simplified);
  return {
    additionalPinyin: graphemeList.map(() => []),
    characters: graphemeList.map((grapheme) => ({ grapheme })),
    morphemeType: "word",
    primaryPinyin: graphemeList.map((grapheme, characterIndex) =>
      isErhuaCharacter({ character: grapheme, characterIndex, word: simplified })
        ? ERHUA_PINYIN
        : getPrimaryPinyin(grapheme),
    ),
  };
}

export function useMorphemeGlyphTileWordMorphemeSlice(
  simplified: string,
): MorphemeGlyphTileWordMorphemeSlice {
  const graphemeList = useMemo(() => runes(simplified), [simplified]);

  // Dedupe before `useQueries`: a word with a repeated character (谢谢, 爸爸, …)
  // would otherwise hand `useQueries` two entries with the same query key
  // (`["graphemePinyin", grapheme]`), which TanStack Query flags as
  // "[QueriesObserver]: Duplicate Queries found." One observer per *unique*
  // grapheme; per-position results are reconstructed from the lookup below.
  const uniqueGraphemeList = useMemo(() => uniq(graphemeList), [graphemeList]);

  // One useQueries call across every unique character: per-character cache
  // entries, and all `load(grapheme)` calls in the same tick coalesce into one
  // batched content-DB query via DataLoader.
  const uniquePinyinResults = useQueries({
    queries: useGraphemePinyinQueryOptionsList(uniqueGraphemeList),
  });

  return useMemo(() => {
    const primaryPinyinByGrapheme = new Map(
      uniqueGraphemeList.map((grapheme, index) => [
        grapheme,
        pinyinReadingsFromPrevalentList(uniquePinyinResults[index]?.data ?? EMPTY_PINYIN_LIST)
          .primaryPinyin,
      ]),
    );
    return buildWordMorphemeGlyphTileSlice(
      simplified,
      (grapheme) => primaryPinyinByGrapheme.get(grapheme) ?? null,
    );
  }, [simplified, uniqueGraphemeList, uniquePinyinResults]);
}
